import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Shift } from '@wilson/interfaces';
import {
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  TimelineRemoveShiftFromUserV2,
} from '@wilson/shift-timeline/state';
import { TimelineCommonOperationsService } from './timeline-common-operations.service';

@Injectable({
  providedIn: 'root',
})
export class DeleteShiftCommonOperationsService {
  private readonly store = inject(Store);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );

  public handleCellViewDeleteOperation(shift: Shift & { id: string }): void {
    this.revalidateIfAssigned(this.removeShiftFromCellViewState(shift));
  }

  public handleStandardViewDeleteOperation(
    shift: Shift & { id: string },
  ): void {
    this.revalidateIfAssigned(this.removeShiftFromStandardViewState(shift));
  }

  /**
   * Removes a shift from cell-view state only, without the productivity/KPI/validation refresh
   * that `handleCellViewDeleteOperation` also does - for bulk callers (multi-select delete) that
   * batch those once across every shift themselves instead of once per shift. Returns the
   * shift's owning user ID, or null when it was unassigned.
   */
  public removeShiftFromCellViewState(
    shift: Shift & { id: string },
  ): string | null {
    if (shift.userId) {
      this.store.dispatch(
        new TimelineRemoveShiftFromUserV2({
          shiftId: shift.id,
          userId: shift.userId,
        }),
      );
      return shift.userId;
    }
    this.store.dispatch(
      new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
        shiftId: shift.id,
      }),
    );
    return null;
  }

  /**
   * Removes a shift from standard-view state only - see `removeShiftFromCellViewState` doc, same
   * reasoning, just standard view's unassigned-region action instead.
   */
  public removeShiftFromStandardViewState(
    shift: Shift & { id: string },
  ): string | null {
    if (shift.userId) {
      this.store.dispatch(
        new TimelineRemoveShiftFromUserV2({
          shiftId: shift.id,
          userId: shift.userId,
        }),
      );
      return shift.userId;
    }
    this.store.dispatch(
      new RemoveShiftWithoutActivitiesFromUnassignedRegion({
        shiftId: shift.id,
      }),
    );
    return null;
  }

  private revalidateIfAssigned(userId: string | null): void {
    if (userId) {
      this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
        userId,
      ]);
      this.timelineCommonOperationsService.revalidateAffectedUsers([userId]);
    }
  }
}
